import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  createSchoolYear,
  updateSchoolYear,
} from "../../../redux/features/schoolYears/schoolYearSlice";

import "./SchoolYearForm.css";

export default function SchoolYearForm({
  mode = "create",

  initialData = null,

  onSuccess,

  onCancel,
}) {
  const dispatch = useDispatch();

  /**
   * REDUX
   */
  const { isCreating, isUpdating, error } = useSelector(
    (state) => state.schoolYears,
  );

  /**
   * FORM STATE
   */
  const [formData, setFormData] = useState({
    name: "",

    start_date: "",

    end_date: "",
  });

  /**
   * VALIDATION ERRORS
   */
  const [validationErrors, setValidationErrors] = useState({});

  /**
   * EDIT MODE
   */
  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData({
        name: initialData.name || "",

        start_date: initialData.start_date?.split("T")[0] || "",

        end_date: initialData.end_date?.split("T")[0] || "",
      });
    }
  }, [mode, initialData]);

  /**
   * CHANGE
   */
  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,

      [name]: value,
    }));

    /**
     * CLEAR FIELD ERROR
     */
    setValidationErrors((prev) => ({
      ...prev,

      [name]: "",
    }));
  };

  /**
   * VALIDATE
   */
  const validateForm = () => {
    const errors = {};

    /**
     * NAME
     */
    if (!formData.name.trim()) {
      errors.name = "Naam is verplicht";
    }

    /**
     * START DATE
     */
    if (!formData.start_date) {
      errors.start_date = "Startdatum is verplicht";
    }

    /**
     * END DATE
     */
    if (!formData.end_date) {
      errors.end_date = "Einddatum is verplicht";
    }

    /**
     * DATE LOGIC
     */
    if (
      formData.start_date &&
      formData.end_date &&
      new Date(formData.start_date) >= new Date(formData.end_date)
    ) {
      errors.end_date = "Einddatum moet later zijn dan startdatum";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /**
   * SUBMIT
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    /**
     * VALIDATION
     */
    if (!validateForm()) {
      return;
    }

    try {
      /**
       * CREATE
       */
      if (mode === "create") {
        await dispatch(createSchoolYear(formData)).unwrap();
      }

      /**
       * UPDATE
       */
      if (mode === "edit") {
        await dispatch(
          updateSchoolYear({
            id: initialData._id,

            data: formData,
          }),
        ).unwrap();
      }

      /**
       * SUCCESS CALLBACK
       */
      onSuccess?.();
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * LOADING
   */
  const isSubmitting = isCreating || isUpdating;

  return (
    <form className="school-year-form" onSubmit={handleSubmit}>
      {/* BACKEND ERROR */}
      {error && <div className="school-year-form__error">{error}</div>}

      {/* NAME */}
      <div className="school-year-form__group">
        <label>Naam</label>

        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="2025 - 2026"
          disabled={isSubmitting}
          className={
            validationErrors.name
              ? "school-year-form__input school-year-form__input--error"
              : "school-year-form__input"
          }
        />

        {validationErrors.name && (
          <span className="school-year-form__validation">
            {validationErrors.name}
          </span>
        )}
      </div>

      {/* START DATE */}
      <div className="school-year-form__group">
        <label>Startdatum</label>

        <input
          type="date"
          name="start_date"
          value={formData.start_date}
          onChange={handleChange}
          disabled={isSubmitting}
          className={
            validationErrors.start_date
              ? "school-year-form__input school-year-form__input--error"
              : "school-year-form__input"
          }
        />

        {validationErrors.start_date && (
          <span className="school-year-form__validation">
            {validationErrors.start_date}
          </span>
        )}
      </div>

      {/* END DATE */}
      <div className="school-year-form__group">
        <label>Einddatum</label>

        <input
          type="date"
          name="end_date"
          value={formData.end_date}
          onChange={handleChange}
          disabled={isSubmitting}
          className={
            validationErrors.end_date
              ? "school-year-form__input school-year-form__input--error"
              : "school-year-form__input"
          }
        />

        {validationErrors.end_date && (
          <span className="school-year-form__validation">
            {validationErrors.end_date}
          </span>
        )}
      </div>

      {/* ACTIONS */}
      <div className="school-year-form__actions">
        <button
          type="button"
          className="school-year-form__cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Annuleren
        </button>

        <button
          type="submit"
          className="school-year-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Opslaan..."
            : mode === "create"
              ? "Schooljaar aanmaken"
              : "Wijzigingen opslaan"}
        </button>
      </div>
    </form>
  );
}
